<template>
  <Teleport v-if="open" to="body">
    <div
      ref="overlayRef"
      class="fixed inset-0 flex min-h-0 overflow-hidden p-4"
      :class="[
        stackOnTop ? 'z-[10050]' : 'z-[100]',
        bottomSheet ? 'items-end justify-center p-0 sm:items-center sm:p-4' : 'items-center justify-center p-4',
      ]"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="headerSlot ? undefined : titleId"
      :aria-label="headerSlot ? title : undefined"
      :aria-describedby="hasDescription ? descriptionId : undefined"
      :data-testid="testId"
      @keydown="onOverlayKeydown"
    >
      <div
        class="absolute inset-0 bg-black/50 backdrop-blur-sm"
        :class="backdropClass"
        aria-hidden="true"
        data-testid="app-modal-backdrop"
        @click="closeOnBackdropClick ? onClose() : null"
      />
      <div v-if="!headerSlot" :id="titleId" class="sr-only">{{ title }}</div>
      <div
        ref="dialogRef"
        class="relative w-full min-h-0 overflow-hidden flex flex-col bg-white dark:bg-zinc-900 border border-slate-200 dark:border-slate-800 shadow-xl"
        :class="[
          bottomSheet ? 'max-h-[88dvh] rounded-t-2xl border-b-0 pb-[env(safe-area-inset-bottom)] sm:max-h-[90dvh] sm:rounded-2xl sm:max-w-md sm:pb-0' : 'max-h-[90dvh] rounded-2xl max-w-md sm:max-w-lg',
          panelClass,
        ]"
        data-testid="app-modal-dialog"
        @click.stop
      >
        <div v-if="bottomSheet" class="shrink-0 pt-3 pb-2 flex justify-center sm:pb-1" aria-hidden="true">
          <div
            class="w-10 h-1 rounded-full bg-slate-300 dark:bg-slate-600"
            :class="sheetHandleClass"
          />
        </div>
        <div
          v-if="headerSlot"
          class="flex items-start justify-between shrink-0 gap-3 px-5 py-3 border-b sm:pt-3"
          :class="headerRowClass"
        >
          <div class="flex-1 min-w-0 pt-0.5">
            <slot name="header" />
          </div>
          <button
            type="button"
            class="shrink-0 p-2 rounded-full transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            :class="closeButtonClass"
            title="Close"
            aria-label="Close"
            data-testid="app-modal-close"
            @click="onClose"
          >
            <span class="material-icons">close</span>
          </button>
        </div>
        <div
          v-else
          class="flex items-center justify-between shrink-0 gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-800 sm:pt-3"
        >
          <h2 class="text-base sm:text-lg font-bold text-charcoal dark:text-white flex-1 min-w-0 truncate">{{ title }}</h2>
          <button
            type="button"
            class="shrink-0 p-2 rounded-full text-slate-500 hover:text-charcoal dark:hover:text-white hover:bg-slate-100 dark:hover:bg-zinc-800 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            title="Close"
            aria-label="Close"
            data-testid="app-modal-close"
            @click="onClose"
          >
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="flex-1 overflow-y-auto overflow-x-hidden px-4 py-4 min-h-0" :class="bodyClass">
          <p
            v-if="hasDescription"
            :id="descriptionId"
            class="text-sm text-slate-600 dark:text-slate-400"
            :class="hasDefaultSlot ? 'sr-only' : 'mb-3'"
          >
            {{ description }}
          </p>
          <slot />
        </div>
        <div
          v-if="$slots.footer"
          class="shrink-0 px-4 py-3 border-t border-slate-200 dark:border-slate-800 flex gap-3 justify-end"
          :class="footerRowClass"
        >
          <slot name="footer" />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount, useSlots } from 'vue'
import { useScrollLock } from '@/composables/useScrollLock'

const props = defineProps({
  open: { type: Boolean, default: false },
  title: { type: String, default: '' },
  description: { type: String, default: '' },
  /** Optional test id for the overlay (default: app-modal-overlay). Use e.g. reservation-modal for E2E. */
  testId: { type: String, default: 'app-modal-overlay' },
  /** When true, modal anchors to bottom on small screens (bottom sheet style). On sm+ it behaves as a normal centered modal. */
  bottomSheet: { type: Boolean, default: false },
  /** When false, clicking the backdrop does not close the modal (only the close button does). Use for verification/critical flows. */
  closeOnBackdropClick: { type: Boolean, default: true },
  /** When true, use a high z-index so the modal appears above stacked UI (e.g. public quick-add sheet at ~10002). */
  stackOnTop: { type: Boolean, default: false },
  /** Extra classes for the dialog panel (e.g. marketing sheet look). */
  panelClass: { type: [String, Array, Object], default: '' },
  backdropClass: { type: [String, Array, Object], default: '' },
  bodyClass: { type: [String, Array, Object], default: '' },
  footerRowClass: { type: [String, Array, Object], default: '' },
  /** When using #header, style the header row (border color etc.). */
  headerRowClass: { type: [String, Array, Object], default: '' },
  closeButtonClass: { type: [String, Array, Object], default: '' },
  sheetHandleClass: { type: [String, Array, Object], default: '' },
})

const emit = defineEmits(['close'])

const slots = useSlots()
const headerSlot = computed(() => typeof slots.header === 'function')
/** True when parent passed default slot content (not only named slots like #footer). */
const hasDefaultSlot = computed(() => typeof slots.default === 'function')
const hasDescription = computed(() => typeof props.description === 'string' && props.description.trim() !== '')

const { lock: lockScroll, unlock: unlockScroll } = useScrollLock()

const overlayRef = ref(null)
const dialogRef = ref(null)
let previousActiveElement = null

const titleId = computed(() => `modal-title-${Math.random().toString(36).slice(2, 9)}`)
const descriptionId = computed(() => `modal-desc-${Math.random().toString(36).slice(2, 9)}`)

function getFocusables(container) {
  if (!container) return []
  const sel = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
  return Array.from(container.querySelectorAll(sel))
}

function onClose() {
  emit('close')
}

function onOverlayKeydown(e) {
  if (e.key === 'Escape') {
    onClose()
    return
  }
  if (e.key !== 'Tab' || !dialogRef.value) return
  const focusables = getFocusables(dialogRef.value)
  if (focusables.length === 0) return
  const current = document.activeElement
  const idx = focusables.indexOf(current)
  if (e.shiftKey) {
    if (idx <= 0) {
      e.preventDefault()
      focusables[focusables.length - 1].focus()
    }
  } else {
    if (idx === -1 || idx === focusables.length - 1) {
      e.preventDefault()
      focusables[0].focus()
    }
  }
}

watch(() => props.open, (isOpen) => {
  if (isOpen) {
    lockScroll()
    previousActiveElement = document.activeElement
    nextTick(() => {
      const focusables = getFocusables(dialogRef.value)
      if (focusables.length > 0) focusables[0].focus()
    })
  } else {
    unlockScroll()
    nextTick(() => {
      if (previousActiveElement && typeof previousActiveElement.focus === 'function') {
        previousActiveElement.focus()
      }
    })
  }
})

onBeforeUnmount(() => {
  unlockScroll()
})
</script>
